import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/core/lib/utils';
import { TaskDialogHeader } from './TaskDialogHeader';
import { TaskTitleEditor } from './TaskTitleEditor';
import { TaskMetadataGrid } from './TaskMetadataGrid';
import { TaskDescriptionEditor } from './TaskDescriptionEditor';
import { DialogSubtaskList } from './DialogSubtaskList';
import { ChecklistSection } from '@/features/checklists/public';
import { TaskTimeEntriesSection } from '@/features/time-tracking/public';
import { TaskAttachments } from './TaskAttachments';
import { TaskDialogSidebar } from './TaskDialogSidebar';
import { TaskActivityPanel } from './TaskActivityPanel';
import { useTaskDialogStore } from '../../hooks/useTaskDialogStore';
import { useTaskDetail } from '../../hooks/useTaskDetail';
import { useTaskAttachments } from '../../hooks/useTaskAttachments';
import { useTaskSubtasks } from '../../hooks/useTaskSubtasks';
import { useTaskTree } from '../../hooks/useTaskTree';
import {
  useUpdateTask,
  useCreateSubtask,
  useUpdateSubtask,
  useDeleteSubtask,
  useUploadAttachment,
  useDeleteAttachment,
} from '../../hooks/useTaskMutations';
import { useAttachTag, useDetachTag } from '../../hooks/useTaskTags';

interface TaskDialogProps {
  taskId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TaskDialog({ taskId, open, onOpenChange }: TaskDialogProps) {
  const { t } = useTranslation('tasks');

  const {
    activeTaskId,
    sidebarOpen,
    activityPanelOpen,
    setActiveTaskId,
    toggleSidebar,
    toggleActivityPanel,
    reset,
  } = useTaskDialogStore();

  useEffect(() => {
    if (open) setActiveTaskId(taskId);
    else reset();
  }, [open, taskId, setActiveTaskId, reset]);

  // modal={false} below works around a documented Radix bug (radix-ui/primitives#1159):
  // react-remove-scroll's body-scroll lock doesn't recognize Portal-rendered content
  // (Popover/Select/Combobox) as part of the dialog's React tree, so any dropdown
  // opened from inside a modal Dialog loses mouse-wheel scrolling. Losing the built-in
  // lock means we replace it with a manual one so the page behind the dialog can't
  // scroll while it's open.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const currentTaskId = activeTaskId ?? taskId;

  const { data: task, isLoading, isError, refetch } = useTaskDetail(open ? currentTaskId : null);

  const { data: taskTree, isLoading: treeLoading } = useTaskTree(open ? taskId : null);
  const { data: attachments, isPending: attachmentsLoading } = useTaskAttachments(
    open ? currentTaskId : null,
  );
  const { data: subtasks, isLoading: subtasksLoading } = useTaskSubtasks(
    open ? currentTaskId : null,
  );
  const updateTask = useUpdateTask(currentTaskId);
  const createSubtask = useCreateSubtask(currentTaskId);
  const updateSubtask = useUpdateSubtask(currentTaskId);
  const deleteSubtask = useDeleteSubtask(currentTaskId);
  const uploadAttachment = useUploadAttachment(currentTaskId);
  const deleteAttachment = useDeleteAttachment(currentTaskId);
  const attachTag = useAttachTag(currentTaskId);
  const detachTag = useDetachTag(currentTaskId);

  async function handleUpload(files: FileList) {
    await Promise.all(Array.from(files).map((f) => uploadAttachment.mutateAsync(f)));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        className="flex h-full max-w-none flex-col overflow-hidden rounded-none p-0 md:h-[min(1000px,92vh)] md:max-w-6xl md:rounded-lg"
        aria-labelledby="task-dialog-title"
        data-testid="tasks-dialog"
        showCloseButton={false}
      >
        <DialogTitle id="task-dialog-title" className="sr-only">
          {t('dialog.title')}
        </DialogTitle>
        {isLoading && (
          <div className="flex flex-1 flex-col gap-4 p-6">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {isError && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="font-medium">{t('dialog.errorTitle')}</p>
            <p className="text-sm text-muted-foreground">{t('dialog.errorBody')}</p>
            <Button variant="outline" onClick={() => refetch()}>
              {t('dialog.retry')}
            </Button>
          </div>
        )}

        {task && (
          <div className="flex flex-1 overflow-hidden">
            <div
              className="overflow-hidden border-r transition-all duration-200"
              style={{ width: sidebarOpen ? 240 : 0 }}
            >
              {sidebarOpen && <TaskDialogSidebar tree={taskTree ?? null} isLoading={treeLoading} />}
            </div>

            <div className="flex flex-1 flex-col overflow-hidden">
              <TaskDialogHeader
                task={task}
                sidebarOpen={sidebarOpen}
                activityPanelOpen={activityPanelOpen}
                onToggleSidebar={toggleSidebar}
                onToggleActivityPanel={toggleActivityPanel}
                onClose={() => onOpenChange(false)}
              />

              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div
                  className={cn(
                    'mx-auto flex flex-col gap-6',
                    sidebarOpen ? 'max-w-2xl' : 'max-w-3xl',
                  )}
                >
                  <TaskTitleEditor task={task} onUpdate={(patch) => updateTask.mutate(patch)} />
                  <TaskMetadataGrid
                    task={task}
                    onUpdate={(patch) => updateTask.mutate(patch)}
                    onAttachTag={(tag) => attachTag.mutate(tag)}
                    onDetachTag={(tagId) => detachTag.mutate(tagId)}
                  />
                  <TaskDescriptionEditor
                    value={task.description ?? ''}
                    onChange={(description) => updateTask.mutate({ description })}
                    saving={updateTask.isPending}
                  />

                  <ChecklistSection taskId={currentTaskId} canConvertToSubtask={!task.parentId} />

                  {!task.parentId && (
                    <DialogSubtaskList
                      subtasks={subtasks ?? []}
                      isLoading={subtasksLoading}
                      projectId={task.projectId ?? ''}
                      boardId={task.status.boardId ?? ''}
                      onCreate={(payload) => createSubtask.mutate(payload)}
                      onUpdate={(id, patch) =>
                        updateSubtask.mutate({ subtaskId: id, payload: patch })
                      }
                      onDelete={(id) => deleteSubtask.mutate(id)}
                    />
                  )}

                  <TaskTimeEntriesSection taskId={currentTaskId} />

                  <TaskAttachments
                    attachments={attachments ?? []}
                    uploading={uploadAttachment.isPending || attachmentsLoading}
                    onUpload={handleUpload}
                    onDelete={(id) => deleteAttachment.mutate(id)}
                  />
                </div>
              </div>
            </div>

            <div
              className="overflow-hidden transition-all duration-200"
              style={{ width: activityPanelOpen ? 320 : 0 }}
            >
              {activityPanelOpen && (
                <TaskActivityPanel taskId={currentTaskId} onClose={toggleActivityPanel} />
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
